/**
 * The Walk photos card's full-screen photo viewer (Claude Design "Led AI -
 * Web Photo Viewer"). Clicking a photo on the Review queue's Walk photos card
 * (`WalkPhotosPane`) opens it; it replaced the inline detail strip.
 *
 *  - Stage: the photo large, CLOSE, `{i} / {n}`, prev / next (and ← / →; Esc
 *    closes), and a filmstrip of the card's photos — the current one ringed
 *    in gold, the rest dimmed.
 *  - Rail: the photo's status (NOT ON A RECORD, or the record it is on with
 *    OPEN →), DESCRIPTION (edited in place, saved on blur), LOCATION (the
 *    project's location directory, searchable, with "No location"), MAKE IT A
 *    for a photo not on a record, and GENERAL (taken on, walk, project).
 *  - Footer: Download and the leave-out square — MinusCircle on a linked
 *    project ("Don't send this photo"), trash on an unlinked one (delete,
 *    after a confirmation the card raises).
 *
 * The words and the icons per integration are the phone's (`WalkPhotoActions`,
 * `walkPhotosModel.convertTargetsFor` / `convertLabel`): Procore makes an
 * Observation, Punch item, Task or Daily log entry; Autodesk only an Issue —
 * "Issue · Observation" or "Issue · Punch List"; an unlinked project the
 * first three. Observation / issue = Eye, punch = Flag, task = Check, daily
 * log = List.
 *
 * Every write is the card's: this file draws and reports intent.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Eye, Flag, List, MapPin, MinusCircle, Search, Trash2, X } from "lucide-react";
import { PhotoImage } from "../components/PhotoImage";
import {
  fetchItemEditOptions,
  type ConsoleWalkPhoto,
  type ItemEditDirectoryOption,
  type WalkPhotoConvertKind,
  type WalkPhotoLocation,
} from "../api";
import { downloadFile, photoFileUrl } from "../lib/photoTransfer";
import { walkPhotoTileTime } from "../../../../backend/src/frontend/pages/visit/walkPhotosModel";
import { FONT_MONO, FONT_TEXT, WPC, WPV } from "../tokens";

export type WalkPhotosIntegration = "procore" | "autodesk" | "local";

const GOLD = WPV.gold;
const STAGE = WPV.stage;
const RAIL = WPV.rail;
const RULE = "1px solid rgba(255,255,255,.07)";
const DIM = "rgba(255,255,255,.45)";
const MONO: CSSProperties = { fontFamily: FONT_MONO, fontWeight: 700, textTransform: "uppercase" };
const RAIL_LABEL: CSSProperties = { ...MONO, fontSize: 9.5, letterSpacing: "1.4px", color: DIM };

// ── per-kind words, icons and tones ──────────────────────────────────────

export type RecordTag = "OBS" | "PUNCH" | "TASK" | "LOG";

/** The console's record-type tokens (the card's ALREADY ON RECORDS tags). */
export const RECORD_TONE: Record<RecordTag, { fg: string; bg: string }> = {
  OBS: { fg: WPC.obsTag, bg: WPC.obsTagBg },
  PUNCH: { fg: WPC.redInk, bg: WPC.punchTagBg },
  LOG: { fg: WPC.logGlyph, bg: WPC.logTagBg },
  TASK: { fg: WPC.ink, bg: WPC.taskTagBg },
};

export function recordTagOf(photo: Pick<ConsoleWalkPhoto, "home">): RecordTag {
  const kind = photo.home?.kind;
  if (kind === "observation" || (kind === "log" && photo.home?.category === "observations")) return "OBS";
  if (kind === "punch") return "PUNCH";
  if (kind === "task") return "TASK";
  return "LOG";
}

/** The phone's icons: observation / issue Eye, punch Flag, task Check, log List. */
export function RecordIcon({ tag, size, color, strokeWidth = 2.3 }: { tag: RecordTag; size: number; color: string; strokeWidth?: number }) {
  const props = { size, color, strokeWidth, "aria-hidden": true as const };
  if (tag === "OBS") return <Eye {...props} />;
  if (tag === "PUNCH") return <Flag {...props} />;
  if (tag === "TASK") return <Check {...props} />;
  return <List {...props} />;
}

const TAG_OF_KIND: Record<WalkPhotoConvertKind, RecordTag> = { observation: "OBS", punch: "PUNCH", task: "TASK", dailyLog: "LOG" };
export const recordTagOfKind = (kind: WalkPhotoConvertKind): RecordTag => TAG_OF_KIND[kind];

/** What the status chip calls a record: Autodesk has Issues and the daily report. */
function recordWord(tag: RecordTag, integration: WalkPhotosIntegration): string {
  if (integration === "autodesk") return tag === "LOG" ? "DAILY REPORT" : tag === "TASK" ? "TASK" : "ISSUE";
  return tag;
}

/** "Daily log entry → which section?" — the console card's list. */
export interface LogSection { key: string; label: string }

type MakeOption =
  | { id: string; label: string; tag: RecordTag; kind: WalkPhotoConvertKind }
  | { id: string; label: string; tag: RecordTag; expands: "sections" | "issue" };

/** The phone's labels for the two Autodesk Issue subtypes (`convertLabel`). */
export const ISSUE_SUBTYPES: Array<{ kind: "observation" | "punch"; label: string }> = [
  { kind: "observation", label: "Issue · Observation" },
  { kind: "punch", label: "Issue · Punch List" },
];

/** MAKE IT A, per integration (the phone's `convertTargetsFor`, plus the
 *  console's daily log entry on Procore). */
export function makeOptionsFor(integration: WalkPhotosIntegration): MakeOption[] {
  if (integration === "autodesk") return [{ id: "issue", label: "Issue", tag: "OBS", expands: "issue" }];
  const three: MakeOption[] = [
    { id: "observation", label: "Observation", tag: "OBS", kind: "observation" },
    { id: "punch", label: "Punch item", tag: "PUNCH", kind: "punch" },
    { id: "task", label: "Task", tag: "TASK", kind: "task" },
  ];
  return integration === "procore" ? [...three, { id: "dailyLog", label: "Daily log entry", tag: "LOG", expands: "sections" }] : three;
}

/** "Monday · Oct 19, 2026 · 1:08a". */
export function takenOnLabel(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${weekday} · ${date} · ${walkPhotoTileTime(iso).toLowerCase()}`;
}

// ── the viewer ───────────────────────────────────────────────────────────

export interface WalkPhotoViewerProps {
  /** The card's photos in its order: NOT ATTACHED, then ALREADY ON RECORDS. */
  lane: ConsoleWalkPhoto[];
  mediaId: string;
  integration: WalkPhotosIntegration;
  visitId: string;
  search: string;
  jobSiteId: string;
  walkName?: string;
  projectName?: string;
  writable: boolean;
  /** A Make it a is in flight. */
  converting: boolean;
  logSections: LogSection[];
  /** Still on this card's Photos cards: can be made into a record, left out or deleted. */
  isLoose: (photo: ConsoleWalkPhoto) => boolean;
  problem?: string | null;
  recordHref?: (itemId: string) => string;
  onOpenRecord?: (itemId: string) => void;
  onPick: (mediaId: string) => void;
  onClose: () => void;
  onSaveDescription: (photo: ConsoleWalkPhoto, text: string) => void;
  onSaveLocation: (photo: ConsoleWalkPhoto, location: WalkPhotoLocation | null) => void;
  onConvert: (photo: ConsoleWalkPhoto, kind: WalkPhotoConvertKind, section?: LogSection) => void;
  /** MinusCircle (linked) or trash (unlinked) — the card decides which it means. */
  onLeaveOut: (photo: ConsoleWalkPhoto) => void;
}

export function WalkPhotoViewer(props: WalkPhotoViewerProps) {
  const { lane, mediaId, integration, onPick, onClose } = props;
  const index = Math.max(0, lane.findIndex((p) => p.mediaId === mediaId));
  const photo = lane[index]!;
  const count = lane.length;
  const [locOpen, setLocOpen] = useState(false);
  const [expanded, setExpanded] = useState<"sections" | "issue" | null>(null);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const local = integration === "local";
  const loose = props.isLoose(photo);

  useEffect(() => {
    setLocOpen(false);
    setExpanded(null);
    setDownloadFailed(false);
  }, [mediaId]);

  const step = (by: number) => {
    if (count < 2) return;
    onPick(lane[(index + by + count) % count]!.mediaId);
  };

  // ← / → page, Esc closes the open menu first, then the viewer. Not while
  // typing: the description and the location search own their keys.
  const keys = useRef({ step, onClose, locOpen, expanded });
  keys.current = { step, onClose, locOpen, expanded };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = !!target && (target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA");
      const k = keys.current;
      if (event.key === "Escape") {
        if (typing) { target!.blur(); return; }
        if (k.locOpen) setLocOpen(false);
        else if (k.expanded) setExpanded(null);
        else k.onClose();
        return;
      }
      if (typing) return;
      if (event.key === "ArrowLeft") k.step(-1);
      else if (event.key === "ArrowRight") k.step(1);
    };
    window.addEventListener("keydown", onKey);
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = held;
    };
  }, []);

  const tag = photo.home && !loose ? recordTagOf(photo) : null;
  const isNew = !!photo.convertedTo && !!photo.home && !photo.home.approved;
  const status = tag
    ? {
        label: `${isNew ? "NEW " : ""}${recordWord(tag, integration)}${photo.home!.title ? ` · ${photo.home!.title.toUpperCase()}` : ""}`,
        fg: RECORD_TONE[tag].fg,
        bg: RECORD_TONE[tag].bg,
        icon: <RecordIcon tag={tag} size={11} color={RECORD_TONE[tag].fg} strokeWidth={2.4} />,
      }
    : { label: "NOT ON A RECORD", fg: "rgba(255,255,255,.7)", bg: "rgba(255,255,255,.08)", icon: null };
  const recordId = tag ? photo.home!.itemId : null;

  const src = photo.url ?? photo.thumbnailUrl;
  const meta: Array<{ k: string; v: string }> = [
    { k: "Taken on", v: takenOnLabel(photo.timestamp) },
    ...(props.walkName ? [{ k: "Walk", v: props.walkName }] : []),
    ...(props.projectName ? [{ k: "Project", v: props.projectName }] : []),
  ].filter((row) => row.v);

  const download = async () => {
    const name = `walk-photo-${walkPhotoTileTime(photo.timestamp).replace(":", "")}.jpg`;
    // The seeded dev loop's photos are inline drawings; everything real goes
    // through the walk's photo file route (same-origin, authenticated).
    const inline = photo.url?.startsWith("data:") ? photo.url : null;
    const ok = inline
      ? await downloadFile(inline, name, { authenticated: false })
      : await downloadFile(photoFileUrl(props.visitId, photo.mediaId, null), name, { authenticated: true });
    setDownloadFailed(!ok);
  };

  const options = makeOptionsFor(integration);
  const locked = props.converting || !props.writable;

  return createPortal(
    <div
      data-testid="walk-photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${count}`}
      onClick={() => { if (locOpen || expanded) { setLocOpen(false); setExpanded(null); } }}
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", background: STAGE, fontFamily: FONT_TEXT, color: WPC.white, WebkitFontSmoothing: "antialiased" }}
    >
      <style>{`.wpv-btn{background:rgba(255,255,255,.08);transition:background .14s}.wpv-btn:hover{background:rgba(255,255,255,.16)}
.wpv-close:hover{background:rgba(255,255,255,.14)}
.wpv-thumb{transition:opacity .14s}.wpv-thumb:hover{opacity:.85!important}
.wpv-field:hover{border-color:rgba(255,255,255,.3)!important}
.wpv-chip:hover:not(:disabled){border-color:rgba(255,255,255,.4)!important}
.wpv-sub:hover:not(:disabled){background:rgba(255,255,255,.08)!important}
.wpv-row:hover{background:${WPV.menuHover}!important}
.wpv-link{color:rgba(255,255,255,.5)}.wpv-link:hover{color:${WPC.white}}
.wpv-dl:hover{background:rgba(255,255,255,.06)!important}
.wpv-out:hover:not(:disabled){background:rgba(217,75,59,.2)!important}
.wpv-desc:empty:before{content:attr(data-ph);color:rgba(255,255,255,.35)}
.wpv-noscroll{scrollbar-width:none}.wpv-noscroll::-webkit-scrollbar{display:none}
.wpv-btn:focus-visible,.wpv-chip:focus-visible,.wpv-thumb:focus-visible,.wpv-field:focus-visible{outline:2px solid ${GOLD};outline-offset:2px}`}</style>

      {/* stage — the dark area around the photo closes the viewer, as Close
          does (the photo, arrows and thumbnails keep their own clicks). With
          a menu open, the first click only closes the menu. */}
      <div
        data-testid="walk-photo-viewer-stage"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target !== e.currentTarget && target.dataset.testid !== "walk-photo-viewer-filmstrip") return;
          if (locOpen || expanded) return;
          onClose();
        }}
        style={{ flex: 1, minWidth: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <button
          type="button"
          data-testid="walk-photo-viewer-close"
          className="wpv-btn wpv-close"
          onClick={onClose}
          style={{ position: "absolute", left: 22, top: 20, zIndex: 1, display: "inline-flex", alignItems: "center", gap: 8, height: 36, padding: "0 14px 0 10px", borderRadius: 18, border: "none", color: WPC.white, cursor: "pointer" }}
        >
          <X size={14} color={WPC.white} strokeWidth={2.2} aria-hidden />
          <span style={{ ...MONO, fontSize: 9.5, letterSpacing: "1.3px" }}>Close</span>
        </button>
        <span data-testid="walk-photo-viewer-count" style={{ ...MONO, position: "absolute", left: 0, right: 0, top: 28, textAlign: "center", fontSize: 10, letterSpacing: "1.4px", color: DIM, pointerEvents: "none" }}>
          {`${index + 1} / ${count}`}
        </span>
        {count > 1 ? (
          <>
            <button type="button" data-testid="walk-photo-viewer-prev" aria-label="Previous photo" className="wpv-btn" onClick={() => step(-1)} style={{ ...roundButton, left: 24 }}>
              <ChevronLeft size={18} color={WPC.white} strokeWidth={2.2} aria-hidden />
            </button>
            <button type="button" data-testid="walk-photo-viewer-next" aria-label="Next photo" className="wpv-btn" onClick={() => step(1)} style={{ ...roundButton, right: 24 }}>
              <ChevronRight size={18} color={WPC.white} strokeWidth={2.2} aria-hidden />
            </button>
          </>
        ) : null}
        {src ? (
          <PhotoImage
            key={photo.mediaId}
            data-testid="walk-photo-viewer-image"
            src={src}
            alt={photo.description || ""}
            style={{ display: "block", maxWidth: "calc(100% - 160px)", maxHeight: "calc(100% - 172px)", objectFit: "contain", borderRadius: 6, boxShadow: "0 30px 80px rgba(0,0,0,.6)" }}
          />
        ) : (
          <span style={{ ...MONO, fontSize: 10, letterSpacing: "1.4px", color: DIM }}>No preview</span>
        )}
        {/* filmstrip */}
        <div data-testid="walk-photo-viewer-filmstrip" className="wpv-noscroll" style={{ position: "absolute", left: 0, right: 0, bottom: 18, display: "flex", justifyContent: "center", gap: 6, padding: "4px 24px", overflowX: "auto" }}>
          {lane.map((p, i) => {
            const on = i === index;
            const thumb = p.thumbnailUrl ?? p.url;
            return (
              <button
                key={p.mediaId}
                type="button"
                data-testid="walk-photo-viewer-thumb"
                data-media-id={p.mediaId}
                data-current={on || undefined}
                aria-label={`Photo ${i + 1}`}
                aria-current={on || undefined}
                className="wpv-thumb"
                onClick={() => onPick(p.mediaId)}
                style={{ position: "relative", flex: "none", width: 44, height: 58, padding: 0, border: "none", borderRadius: 6, overflow: "hidden", background: WPV.thumb, cursor: "pointer", opacity: on ? 1 : 0.5, boxShadow: on ? `0 0 0 2px ${GOLD}` : "none" }}
              >
                {thumb ? <PhotoImage src={thumb} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* info rail */}
      <div
        data-testid="walk-photo-viewer-rail"
        onClick={(e) => { e.stopPropagation(); if (locOpen || expanded) { setLocOpen(false); setExpanded(null); } }}
        style={{ flex: "none", width: 380, background: RAIL, borderLeft: RULE, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <div className="wpv-noscroll" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {/* status */}
          <div style={{ padding: "22px 26px 0", display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span
              data-testid="walk-photo-viewer-status"
              title={tag ? photo.home!.title : undefined}
              style={{ ...MONO, display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, height: 24, padding: "0 9px", borderRadius: 6, background: status.bg, color: status.fg, fontSize: 9, letterSpacing: "1.2px" }}
            >
              {status.icon}
              <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{status.label}</span>
            </span>
            {recordId && props.recordHref ? (
              <a
                href={props.recordHref(recordId)}
                data-testid="walk-photo-viewer-open-record"
                className="wpv-link"
                onClick={(e) => {
                  if (!props.onOpenRecord || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  onClose();
                  props.onOpenRecord(recordId);
                }}
                style={{ ...MONO, flex: "none", fontSize: 9, letterSpacing: "1.2px", textDecoration: "none", whiteSpace: "nowrap" }}
              >
                Open →
              </a>
            ) : null}
          </div>

          {/* description */}
          <div style={{ padding: "20px 26px 22px", borderBottom: RULE }}>
            <div style={{ ...RAIL_LABEL, color: GOLD }}>Description</div>
            <div
              key={`${photo.mediaId}:${photo.description}`}
              role="textbox"
              aria-label="Photo description"
              data-testid="walk-photo-viewer-description"
              className="wpv-desc"
              contentEditable={props.writable}
              suppressContentEditableWarning
              data-ph={photo.descriptionPending ? "Description pending…" : "Add a description"}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => props.onSaveDescription(photo, e.currentTarget.innerText.trim())}
              style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.5, marginTop: 10, outline: "none", cursor: props.writable ? "text" : "default", minHeight: 25, overflowWrap: "anywhere", textWrap: "pretty" } as CSSProperties}
            >
              {photo.description}
            </div>
          </div>

          {/* location */}
          <div style={{ position: "relative", padding: "18px 26px", borderBottom: RULE }}>
            <div style={RAIL_LABEL}>Location</div>
            <button
              type="button"
              data-testid="walk-photo-viewer-location"
              className="wpv-field"
              disabled={!props.writable}
              aria-expanded={locOpen}
              aria-haspopup="listbox"
              onClick={(e) => { e.stopPropagation(); setExpanded(null); setLocOpen((open) => !open); }}
              style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", marginTop: 10, height: 40, padding: "0 10px 0 12px", borderRadius: 10, background: locOpen ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.05)", border: `1px solid ${locOpen ? GOLD : "rgba(255,255,255,.12)"}`, cursor: props.writable ? "pointer" : "default", fontFamily: FONT_TEXT, textAlign: "left" }}
            >
              <MapPin size={14} color={photo.location ? GOLD : "rgba(255,255,255,.5)"} strokeWidth={2.2} aria-hidden style={{ flex: "none" }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: photo.location ? WPC.white : "rgba(255,255,255,.5)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {photo.location ? photo.location.name : "Add location"}
              </span>
              <ChevronDown size={12} color="rgba(255,255,255,.5)" strokeWidth={2.2} aria-hidden style={{ flex: "none" }} />
            </button>
            {locOpen ? (
              <ViewerLocationMenu
                current={photo.location}
                jobSiteId={props.jobSiteId}
                visitId={props.visitId}
                search={props.search}
                onPick={(location) => { setLocOpen(false); props.onSaveLocation(photo, location); }}
              />
            ) : null}
          </div>

          {/* make it a */}
          {loose ? (
            <div style={{ padding: "18px 26px", borderBottom: RULE }}>
              <div style={RAIL_LABEL}>{props.converting ? "Making it…" : "Make it a"}</div>
              <div role="menu" aria-label="Make it a" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {options.map((option) => {
                  const on = "expands" in option && expanded === option.expands;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="menuitem"
                      data-testid={`walk-photo-viewer-make-${option.id}`}
                      aria-expanded={"expands" in option ? on : undefined}
                      className="wpv-chip"
                      disabled={locked}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocOpen(false);
                        if ("expands" in option) setExpanded(on ? null : option.expands);
                        else props.onConvert(photo, option.kind);
                      }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 12px 0 10px", borderRadius: 17, background: on ? GOLD : "rgba(255,255,255,.05)", border: `1px solid ${on ? GOLD : "rgba(255,255,255,.14)"}`, color: on ? WPC.ink : WPC.white, fontFamily: FONT_TEXT, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", cursor: locked ? "default" : "pointer", opacity: locked && !props.converting ? 0.55 : 1, transition: "border-color .14s" }}
                    >
                      <RecordIcon tag={option.tag} size={13} color={on ? WPC.ink : WPC.white} />
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {expanded ? (
                <div
                  data-testid={expanded === "sections" ? "walk-photo-viewer-sections" : "walk-photo-viewer-issue-types"}
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginTop: 10, padding: 10, borderRadius: 12, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)", animation: "wpvPop .14s both" }}
                >
                  <style>{"@keyframes wpvPop{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}"}</style>
                  <div style={{ ...MONO, fontSize: 8.5, letterSpacing: "1.2px", color: DIM, padding: "2px 4px 8px" }}>
                    {expanded === "sections" ? "Which daily log section?" : "Which issue type?"}
                  </div>
                  <div role="menu" aria-label={expanded === "sections" ? "Daily log section" : "Issue type"} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(expanded === "sections"
                      ? props.logSections.map((section) => ({ id: `section-${section.key}`, label: section.label, run: () => props.onConvert(photo, "dailyLog", section) }))
                      : ISSUE_SUBTYPES.map((sub) => ({ id: `issue-${sub.kind}`, label: sub.label, run: () => props.onConvert(photo, sub.kind) }))
                    ).map((choice) => (
                      <button
                        key={choice.id}
                        type="button"
                        role="menuitem"
                        data-testid={`walk-photo-viewer-${choice.id}`}
                        className="wpv-sub"
                        disabled={locked}
                        onClick={choice.run}
                        style={{ display: "inline-flex", alignItems: "center", height: 30, padding: "0 11px", borderRadius: 15, border: "1px solid rgba(255,255,255,.14)", background: "transparent", color: WPC.white, fontFamily: FONT_TEXT, fontSize: 12.5, fontWeight: 600, cursor: locked ? "default" : "pointer", whiteSpace: "nowrap" }}
                      >
                        {choice.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {props.problem ? (
            <div role="alert" data-testid="walk-photo-viewer-problem" style={{ margin: "14px 26px 0", fontSize: 13, lineHeight: 1.4, color: WPV.problem }}>{props.problem}</div>
          ) : null}

          {/* general */}
          <div style={{ padding: "18px 26px 22px" }}>
            <div style={RAIL_LABEL}>General</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              {meta.map((row) => (
                <div key={row.k} data-testid="walk-photo-viewer-meta" data-k={row.k}>
                  <div style={{ fontSize: 12.5, color: DIM }}>{row.k}</div>
                  <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 2, overflowWrap: "anywhere" }}>{row.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ flex: "none", display: "flex", gap: 10, padding: "14px 26px 22px", borderTop: RULE }}>
          <button
            type="button"
            data-testid="walk-photo-viewer-download"
            className="wpv-dl"
            onClick={() => void download()}
            style={{ flex: 1, height: 44, borderRadius: 12, border: `1px solid ${downloadFailed ? WPC.red : "rgba(255,255,255,.14)"}`, background: "transparent", color: downloadFailed ? WPV.problem : WPC.white, fontFamily: FONT_TEXT, fontSize: 14.5, fontWeight: 700, cursor: "pointer" }}
          >
            {downloadFailed ? "Download failed — try again" : "Download"}
          </button>
          {loose && props.writable ? (
            <button
              type="button"
              data-testid="walk-photo-viewer-leave-out"
              aria-label={local ? "Delete photo" : "Leave this photo out"}
              title={local ? "Delete photo" : "Don't send this photo"}
              className="wpv-out"
              disabled={props.converting}
              onClick={() => props.onLeaveOut(photo)}
              style={{ flex: "none", width: 44, height: 44, padding: 0, borderRadius: 12, border: "1px solid rgba(255,255,255,.14)", background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              {local ? <Trash2 size={16} color={WPV.out} strokeWidth={2} aria-hidden /> : <MinusCircle size={17} color={WPV.out} strokeWidth={2} aria-hidden />}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const roundButton: CSSProperties = {
  position: "absolute",
  top: "50%",
  marginTop: -24,
  zIndex: 1,
  width: 48,
  height: 48,
  padding: 0,
  borderRadius: 24,
  border: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

// ── the location dropdown ────────────────────────────────────────────────

/** "Level 2 › Unit 214" → ["Level 2", "Unit 214"]. */
function pathParts(option: { name: string; path?: string }): string[] {
  const parts = (option.path || option.name).split(/\s*(?:›|>|\/)\s*/).filter(Boolean);
  return parts.length ? parts : [option.name];
}

/** The directory as a tree: each location under its parent, indented by
 *  depth. Parents are bold, leaves regular (the design's dropdown). */
export function locationRows(options: ItemEditDirectoryOption[], query: string): Array<ItemEditDirectoryOption & { depth: number; parent: boolean }> {
  const parts = new Map(options.map((o) => [o.remoteId, pathParts(o)]));
  const keyOf = (o: ItemEditDirectoryOption) => parts.get(o.remoteId)!.join(" › ").toLowerCase();
  const keys = options.map(keyOf);
  const q = query.trim().toLowerCase();
  return [...options]
    .sort((a, b) => keyOf(a).localeCompare(keyOf(b)))
    .filter((o) => !q || `${o.path ?? ""} ${o.name}`.toLowerCase().includes(q))
    .map((o) => {
      const key = keyOf(o);
      // Indented under the ancestors the directory actually lists — a
      // location whose parent is not in it starts at the left edge.
      const depth = keys.filter((k) => k !== key && key.startsWith(`${k} › `)).length;
      return { ...o, depth, parent: keys.some((k) => k !== key && k.startsWith(`${key} › `)) };
    });
}

function ViewerLocationMenu(props: {
  current?: WalkPhotoLocation;
  jobSiteId: string;
  visitId: string;
  search: string;
  onPick: (location: WalkPhotoLocation | null) => void;
}) {
  const { jobSiteId, visitId, search } = props;
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ItemEditDirectoryOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchItemEditOptions(jobSiteId, "photos", search, visitId)
      .then((found) => !cancelled && setOptions(found.locations))
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
          setFailed(true);
        }
      });
    input.current?.focus();
    return () => {
      cancelled = true;
    };
  }, [jobSiteId, visitId, search]);

  const rows = useMemo(() => locationRows(options ?? [], query), [options, query]);
  const typed = query.trim();
  const isCurrent = (o: ItemEditDirectoryOption) =>
    props.current?.procoreLocationId ? props.current.procoreLocationId === o.remoteId : !!props.current && !props.current.procoreLocationId && props.current.name === o.name;

  const row = (key: string, children: ReactNode, onClick: () => void, extra: CSSProperties = {}, testId?: string, selected = false) => (
    <button
      key={key}
      type="button"
      role="option"
      aria-selected={selected}
      data-testid={testId}
      className="wpv-row"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", height: 36, padding: "0 10px", borderRadius: 7, border: "none", background: selected ? WPV.menuSelected : "transparent", fontFamily: FONT_TEXT, fontSize: 13.5, color: WPC.ink, cursor: "pointer", textAlign: "left", ...extra }}
    >
      {children}
    </button>
  );

  return (
    <div
      data-testid="walk-photo-viewer-location-menu"
      onClick={(e) => e.stopPropagation()}
      style={{ position: "absolute", left: 26, right: 26, top: 92, zIndex: 20, background: WPV.menu, borderRadius: 12, boxShadow: "0 20px 50px rgba(0,0,0,.5)", overflow: "hidden", color: WPC.ink, animation: "wpvPop .14s ease-out both" }}
    >
      <style>{"@keyframes wpvPop{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}"}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 42, padding: "0 12px", borderBottom: "1px solid rgba(20,17,12,.08)" }}>
        <Search size={13} color={WPC.label} strokeWidth={2} aria-hidden />
        <input
          ref={input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && typed && rows.length === 0) props.onPick({ name: typed, freeText: true }); }}
          placeholder="Search locations"
          aria-label="Search locations"
          style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: FONT_TEXT, fontSize: 13.5, color: WPC.ink }}
        />
      </div>
      <div role="listbox" aria-label="Locations" className="wpv-noscroll" style={{ maxHeight: 260, overflowY: "auto", padding: 5 }}>
        {options === null ? (
          <div style={{ padding: 10, fontSize: 13, color: WPC.label }}>Loading…</div>
        ) : (
          rows.map((o) => {
            const on = isCurrent(o);
            return row(
              o.remoteId,
              <>
                <span style={{ flex: 1, minWidth: 0, fontWeight: o.parent ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.name}</span>
                {on ? <Check size={12} color={WPC.ink} strokeWidth={2.6} aria-hidden /> : null}
              </>,
              () => props.onPick({ name: o.name, ...(o.path ? { path: o.path } : {}), procoreLocationId: o.remoteId, freeText: false }),
              { paddingLeft: 10 + Math.min(o.depth, 4) * 14 },
              "walk-photo-viewer-location-option",
              on,
            );
          })
        )}
        {options !== null && rows.length === 0 ? (
          typed ? (
            row("typed", <span>Use “{typed}”</span>, () => props.onPick({ name: typed, freeText: true }))
          ) : (
            <div style={{ padding: 10, fontSize: 13, color: WPC.label }}>{failed ? "Locations are unavailable." : "No locations yet. Type one to use it."}</div>
          )
        ) : null}
      </div>
      <button
        type="button"
        data-testid="walk-photo-viewer-location-none"
        className="wpv-row"
        disabled={!props.current}
        onClick={(e) => { e.stopPropagation(); props.onPick(null); }}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", height: 38, padding: "0 14px", border: "none", borderTop: "1px solid rgba(20,17,12,.08)", background: "transparent", fontFamily: FONT_TEXT, fontSize: 13, color: WPC.sub, cursor: props.current ? "pointer" : "default", textAlign: "left" }}
      >
        <X size={11} color={WPC.label} strokeWidth={2.4} aria-hidden />
        No location
      </button>
    </div>
  );
}
